import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCanvasStore } from './canvas-store';
import { isDesktopAgentBridge, type DesktopAgentApproval, type DesktopAgentState } from './desktop-agent-transport';
import { registerDesktopRuntime, reconcileDesktopRuntime, stopDesktopRuntime } from './desktop-runtime-session';

export function useDesktopAgent(enabled: boolean, projectId: number | null, projectLoading: boolean, authenticatedAccountId: number | null) {
  const bridge = enabled && typeof window !== 'undefined' && window.location.pathname === '/agent' && isDesktopAgentBridge(window.admojiLocal) ? window.admojiLocal : null;
  const [state, setState] = useState<DesktopAgentState | null>(null);
  const [approvals, setApprovals] = useState<DesktopAgentApproval[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const latest = useRef<DesktopAgentState | null>(null);
  const account = useRef(authenticatedAccountId);
  account.current = authenticatedAccountId;
  const epoch = useRef(0);
  const stoppingMismatch = useRef<string | null>(null);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!bridge) return;
    return registerDesktopRuntime(bridge, () => ({ accountId: account.current, taskId: useCanvasStore.getState().activeProjectId }));
  }, [bridge]);
  useEffect(() => { reconcileDesktopRuntime(); }, [projectId, authenticatedAccountId]);
  const receive = useCallback((next: DesktopAgentState) => {
    const accountId = account.current;
    if (!Number.isSafeInteger(accountId) || accountId! < 1 || next.accountId !== accountId) {
      latest.current = null;
      setState(null); setApprovals([]);
      // A transient unknown web identity has no authority to stop a run.
      // A known different identity must not keep another account's run alive.
      if (accountId != null && next.accountId != null && next.accountId !== accountId && next.running && bridge) {
        const key = `${next.accountId}:${next.runId}`;
        if (stoppingMismatch.current !== key) {
          stoppingMismatch.current = key;
          void bridge.stop().then(() => bridge.getState()).then(receive).catch(() => {});
        }
      }
      return;
    }
    stoppingMismatch.current = null;
    const previous = latest.current;
    latest.current = next;
    setState(next);
    if (Array.isArray(next.approvals)) setApprovals(next.approvals);
    if (previous?.creditsBalance !== next.creditsBalance || (previous?.running && !next.running)) {
      void queryClient.invalidateQueries({ queryKey: ['/api/usage/balance'] });
    }
  }, [bridge, queryClient]);

  useEffect(() => {
    const generation = ++epoch.current;
    latest.current = null; setState(null); setApprovals([]); setError(''); setBusy(false);
    if (!bridge) return;
    let alive = true;
    const valid = () => alive && epoch.current === generation;
    const off = [
      bridge.onState(next => { if (valid()) receive(next); }),
      bridge.onApproval(request => {
        const owner = latest.current;
        if (valid() && owner?.accountId === account.current && owner?.accountId != null && request.taskId === owner.taskId && request.runId === owner.runId) setApprovals(current => [...current.filter(item => item.id !== request.id), request]);
      }),
      bridge.onApprovalClosed(id => { if (valid()) setApprovals(current => current.filter(item => item.id !== id)); }),
    ];
    bridge.getState().then(next => { if (valid()) receive(next); }).catch(() => { if (valid()) setError('Could not connect to the desktop agent. Reopen AI Agent to try again.'); });
    return () => { alive = false; off.forEach(unsubscribe => unsubscribe()); };
  }, [bridge, receive, authenticatedAccountId]);

  const command = useCallback(async (action: () => Promise<unknown>) => {
    const owner = account.current;
    if (!Number.isSafeInteger(owner) || owner! < 1) return;
    setError(''); setBusy(true);
    try { await action(); } catch (failure) {
      if (account.current === owner) setError(failure instanceof Error ? failure.message.replace(/^Error invoking remote method '[^']+': (?:Error: )?/, '') : 'The desktop action could not finish.');
    } finally { setBusy(false); }
  }, []);

  const authenticatedAccount = Number.isSafeInteger(authenticatedAccountId) && authenticatedAccountId! > 0;
  const safeState = authenticatedAccount && state?.accountId === authenticatedAccountId ? state : null;
  return {
    available: !!bridge,
    authenticatedAccount,
    state: safeState,
    approvals: safeState ? approvals.filter(request => request.taskId === projectId && request.runId === safeState.runId) : [],
    error: safeState ? error : '', busy,
    blocked: false,
    runningHere: !!bridge && !!safeState?.running && safeState.taskId === projectId,
    chooseFolder: () => command(async () => { if (bridge) receive(await bridge.chooseFolder()); }),
    stop: () => command(async () => {
      stopDesktopRuntime(account.current, safeState?.taskId ?? projectId);
      if (bridge && safeState?.runId && safeState.taskId != null) await bridge.endTurn({ accountId: safeState.accountId!, taskId: safeState.taskId, runId: safeState.runId });
    }),
    decide: (id: string, outcome: 'allowed-once' | 'rejected') => command(async () => { await bridge?.decide(id, outcome); }),
  };
}

export type DesktopAgentController = ReturnType<typeof useDesktopAgent>;
